import React from 'react'
import { Grid, FormControl, InputLabel, MenuItem, Select, FormHelperText, Box, Typography, Switch } from '@mui/material'
import { type AssetProjectUpdateDto } from '../../../api-schema-types/assets/AssetProjectCrudDtos'
import { FormikFieldErrors } from '../../components/ui-widgets/FormikFieldErrors'
import FormikTextField from '../../components/ui-widgets/FormikTextField'

import { type EditSolarProjectComponentProps } from './EditSolarProjectForm'
import { type FormikContextType, useFormikContext } from 'formik'

const EditWebsiteLocation: React.FC<EditSolarProjectComponentProps> = ({ project, overrideLockedFields }) => {
  const formikProps: FormikContextType<AssetProjectUpdateDto> = useFormikContext()
  const formikErrors = new FormikFieldErrors(formikProps)

  const isWebsiteLocked = (): boolean => {
    return !overrideLockedFields && project.website.isLocked
  }

  const isOrganisationsLocked = (): boolean => {
    return !overrideLockedFields && project.organisations.isLocked
  }

  const isLocationLocked = (): boolean => {
    return !overrideLockedFields && project.location.isLocked
  }

  const handleFeatureChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isWebsiteLocked()) {
      return
    }
    formikProps.setFieldValue('website.featured', !formikProps.values.website.featured)
  }

  return (
    <>
      <Box sx={{ pb: 4 }}>
        <h3>Website</h3>
        <Grid container spacing={4}>

          {/* ==================================================================================================== */}

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'website.carbonOffsetRate'}
                             label={'Carbon Offset Rate'} type={'number'}
                             locked={isWebsiteLocked()}/>
          </Grid>

          <Grid item md={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>Sector</InputLabel>
              <Select
                {...formikProps.getFieldProps('website.marketingSector')}
                label={'Charity'}
                disabled={isOrganisationsLocked()}
                error={formikErrors.fieldInError('website.marketingSector')}
              >
                <MenuItem key={''} value={''}>&nbsp;</MenuItem>
                {
                  project.lookups.marketingSectors.map((c) =>
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormHelperText>{formikErrors.getError('website.marketingSector')}</FormHelperText>
          </Grid>

          <Grid item md={3}></Grid>
          <Grid item md={3}></Grid>

          {/* ==================================================================================================== */}

          <Grid item md={12}>
            <FormikTextField formikProps={formikProps}
                             fieldName={'website.investmentReturnGraphData'}
                             label={'Investment Return Graph Data'} type={'text'} multiline={true}
                             locked={isWebsiteLocked()}/>
          </Grid>

        </Grid>
      </Box>

      {/* ==================================================================================================== */}

      <Box sx={{ pb: 4 }}>
        <h3>CMS</h3>

        <Grid container spacing={4} sx={{ pt: 4 }}>
          <Grid item md={6}>
            <FormikTextField formikProps={formikProps} fieldName={'website.description'} label={'Description'} type={'text'}
                             locked={isWebsiteLocked()} multiline={true} />
          </Grid>
          <Grid item md={6}>
            <FormikTextField formikProps={formikProps} fieldName={'website.summary'} label={'Summary'} type={'text'}
                             locked={isWebsiteLocked()} multiline={true} />
          </Grid>
          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'website.videoLink'} label={'Video Link'} type={'text'}
                             locked={isWebsiteLocked()} />
          </Grid>
          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'website.brochureUrl'} label={'Brochure URL'} type={'text'}
                             locked={isWebsiteLocked()} />
          </Grid>
          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'website.mainImageUrl'} label={'Main Image URL'} type={'text'}
                             locked={isWebsiteLocked()} />
          </Grid>
          <Grid item md={3}>
            <Typography variant={'body2'}>Featured</Typography>
            <Switch
              checked={formikProps.values.website.featured}
              onChange={handleFeatureChange}
              color="primary"
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ pb: 4 }}>
        <h3>Organisations</h3>

        <Grid container spacing={4}>
          <Grid item md={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>Charity</InputLabel>
              <Select
                {...formikProps.getFieldProps('organisations.charityMemberId')}
                label={'Charity'}
                disabled={isOrganisationsLocked()}
                error={formikErrors.fieldInError('organisations.charityMemberId')}
              >
                <MenuItem key={0} value={0}>&nbsp;</MenuItem>
                {
                  project.lookups.charities.map((c) =>
                    <MenuItem key={c.assetHolderId} value={c.assetHolderId}>{c.organisationName}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormHelperText>{formikErrors.getError('organisations.charityMemberId')}</FormHelperText>
          </Grid>

          <Grid item md={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>Solar Consumer</InputLabel>
              <Select
                {...formikProps.getFieldProps('organisations.solarConsumerId')}
                label={'Solar Consumer'}
                disabled={isOrganisationsLocked()}
                error={formikErrors.fieldInError('organisations.solarConsumerId')}
              >
                <MenuItem key={0} value={0}>&nbsp;</MenuItem>
                {
                  project.lookups.solarConsumers.map((lsc) =>
                    <MenuItem key={lsc.solarConsumerId} value={lsc.solarConsumerId}>{lsc.organisationName}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormHelperText>{formikErrors.getError('organisations.solarConsumerId')}</FormHelperText>
          </Grid>

          <Grid item md={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>Solar Installer</InputLabel>
              <Select
                {...formikProps.getFieldProps('organisations.solarInstallerId')}
                label={'Solar Installer'}
                disabled={isOrganisationsLocked()}
                error={formikErrors.fieldInError('organisations.solarInstallerId')}
              >
                <MenuItem key={0} value={0}>&nbsp;</MenuItem>
                {
                  project.lookups.solarInstallers.map((lsi) =>
                    <MenuItem key={lsi.solarInstallerId} value={lsi.solarInstallerId}>{lsi.organisationName}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormHelperText>{formikErrors.getError('organisations.solarInstallerId')}</FormHelperText>
          </Grid>

        </Grid>

      </Box>

      {/* ==================================================================================================== */}

      {/* <Divider/> */}

      <Box sx={{ pb: 4 }}>
        <h3>Location</h3>

        <Grid container spacing={4}>

          {/* ==================================================================================================== */}

          <Grid item md={6}>
            <FormikTextField formikProps={formikProps} fieldName={'location.addressLine1'} label={'Address Line 1'}
                             type={'text'} locked={isLocationLocked()}/>
          </Grid>

          <Grid item md={6}>
            <FormikTextField formikProps={formikProps} fieldName={'location.addressLine2'} label={'Address Line 2'}
                             type={'text'} locked={isLocationLocked()}/>
          </Grid>

          <Grid item md={6}>
            <FormikTextField formikProps={formikProps} fieldName={'location.city'} label={'City'}
                             type={'text'} locked={isLocationLocked()}/>
          </Grid>

          <Grid item md={6}>
            <FormikTextField formikProps={formikProps} fieldName={'location.stateOrProvince'} label={'State Or Province'}
                             type={'text'} locked={isLocationLocked()}/>
          </Grid>

          <Grid item md={6}>
            <FormikTextField formikProps={formikProps} fieldName={'location.postalCode'} label={'Postal Code'}
                             type={'text'} locked={isLocationLocked()}/>
          </Grid>

          <Grid item md={6}>
            <Grid item md={6}>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel>Country</InputLabel>
                <Select
                  {...formikProps.getFieldProps('location.countryId')}
                  label={'Country'}
                  disabled={isLocationLocked()}
                  error={formikErrors.fieldInError('location.countryId')}
                >
                  <MenuItem key={0} value={0}>&nbsp;</MenuItem>
                  {
                    project.lookups.countries.map((c) =>
                      <MenuItem key={c.countryId} value={c.countryId}>{c.countryName}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>
              <FormHelperText>{formikErrors.getError('location.countryId')}</FormHelperText>
            </Grid>

          </Grid>

          {/* ==================================================================================================== */}

          <Grid item md={6}>
            <FormikTextField formikProps={formikProps} fieldName={'location.embeddedMapUrl'} label={'Embedded Map Url'}
                             type={'text'} locked={isLocationLocked()}/>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default EditWebsiteLocation
